import type { PropertyValues, TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { EntityContent } from 'src/content/entity-content';
import type { ModelEvent } from 'src/store/selectors';
import { isEmptyHtmlContent, isSameHtmlContent, joinWithAnd, toDateTimeDuration } from 'src/utilities/text';
import { t } from 'src/i18n';
import { shortenPath } from 'src/utilities/path';
import { RecurrenceOptionsDialog } from 'src/content/event-occurrences/recurrence-options-dialog';
import { uniq, xor } from 'lodash';
import { AlertsOnChangeDialog } from 'src/content/alerts-on-change-dialog';
import { DEditOccurrencesDialog } from 'src/layout/parts/d-edit-occurrences-dialog';

import 'src/library/editors/components/d-edit-event-timing';
import 'src/library/editors/components/d-edit-html';
import '../../library/fields/d-view-html';
import '../../library/fields/d-view-occurrences';
import 'src/library/editors/elements/d-select-checkbox';
import './../d-edit-entity-name';
import './d-edit-assignees';
import './d-edit-reminder';
import '../../library/lists/d-list-section';
import '../../library/elements/d-smooth-resize';
import type { DEditEntityName } from 'src/content/d-edit-entity-name';
import { LocalDate } from 'src/utilities/local-date';
import type { UpdateSectionItem } from '../update-section-item';
import type { ModelForEntityName } from '../model-for-entity-name';
import type { AlertsOnChangeResult } from '../alerts-on-change-result';
import type { Occurrence } from '../../library/lists/occurrence';
import type { EventTiming } from '../../library/editors/components/event-timing';
import type { ListDefinition } from '../list-definition';
import type { ReminderType } from './reminder-type';
import type { SaveType } from './save-type';
import type { EventDoneDetail } from './event-done-detail';
import type { EventOccurrenceView } from './event-occurrence-view';
import type { EventOccurrenceViewEditItem } from './event-occurrence-view-edit-item';
import type { RecurrenceOptionsResult } from './recurrence-options-result';
import type { OccurrencesDialogResult } from '../../layout/parts/occurrences-dialog-result';
import type { SectionField } from '../../library/components/section-field';
import { assertIsDefined } from '../../assertIsDefined';

/**
 *
 *
 *
 *
 *
 */
@customElement('d-event-occurrence-view')
export class DEventOccurrenceView extends EntityContent<EventOccurrenceView, EventOccurrenceViewEditItem> {
  @query('d-edit-entity-name')
  nameEditor!: DEditEntityName;
  @state()
  private assetListOpened = false;
  @state()
  private newFromTask = false;
  @state()
  private editDoneBy = '';
  @state()
  private editDoneDate = '';

  // Prefill carried via URL params when a task is created from another entity
  // (e.g. «Planlegg gjennomgang»). Captured before the query is stripped and
  // applied in calculateEditItem, so the edit item is correct from the first
  // render — avoids a race where the name editor's autofocus resets
  // nameConfirmed and the task gets stuck on the routine-selection step.
  @state()
  private prefillNameConfirmed = false;
  private prefillTime: string | undefined = undefined;
  private prefillReminder: ReminderType | undefined = undefined;

  // Scheduling a review of a task, or listing review tasks for a task, makes no
  // sense for the task/event occurrence view itself.
  protected get showPlanReviewAction(): boolean {
    return false;
  }

  protected get showReviewTasksSection(): boolean {
    return false;
  }

  protected get currentItemLabel() {
    return this.editItem?.name ?? this.entityView.name;
  }

  protected get currentItemSublabel() {
    return this.entityView.newItem ? '' : this.entityView.timeDescription;
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('events', this.viewFields(), this.entityView)} `;
  }

  editHeaderActions() {
    if (this.editItem && !this.editItem.nameConfirmed) {
      return [];
    }
    return super.editHeaderActions();
  }

  renderRelatedRiskAssessments() {
    const assessments = this.entityView.relatedRiskAssessments;
    if (assessments.length > 0) {
      return html`
        <div class="related-entities-section risk-assessment-section">
          <div>
            ${assessments.length > 1 ? t('events.measureInRiskAssessments') : t('events.measureInRiskAssessment')}
            ${this.entityView.relatedRiskAssessments.map((r) => {
              return html` <a href="${r.href}">${r.name}</a> `;
            })}
          </div>
        </div>
      `;
    }
  }

  renderPreContent() {
    if (!this.entityView.startTaskUuid) {
      return html`${this.renderRelatedRiskAssessments()}${this.renderMetaSection()}`;
    }
    return html``;
  }

  async onDelete() {
    let saveType: SaveType = 'single';
    if (this.entityView.canUpdateFutureOccurrences) {
      const result: RecurrenceOptionsResult = await RecurrenceOptionsDialog.open({
        isMeeting: false,
        deleting: true,
      });
      if (result.action === 'cancel') {
        return;
      } else if (result.action === 'following') {
        saveType = 'followingRepeated';
      } else {
        saveType = 'singleRepeated';
      }
    }

    let sendAlerts = false;
    const originalEditItem = this.calculateEditItem();

    if (this.qualifiesForDeleteAlerts(originalEditItem)) {
      const r = this.alertOptionsAccessSettings(originalEditItem);
      const result: AlertsOnChangeResult = await AlertsOnChangeDialog.open({
        taskEvent: false,
        noAccessMessage: r.noAccessMessage,
        alertsDisabled: r.alertsDisabled,
        noDetails: !this.hasDetails(originalEditItem),
        isDelete: false,
        newItem: this.entityView.newItem,
      });
      if (result.action === 'save-and-alert') {
        sendAlerts = true;
      } else if (result.action === 'cancel') {
        return;
      }
    }

    this.fireDeleteEvent(saveType, sendAlerts);
  }

  hasDetails(item: EventOccurrenceViewEditItem): boolean {
    return item.notes !== '';
  }

  async initializeEditItem() {
    this.editItem = this.calculateEditItem();
  }

  calculateEditItem(): EventOccurrenceViewEditItem {
    let assignedToEmployees = this.entityView.assignedToEmployees;
    let date = this.entityView.date;
    let time = this.entityView.time;
    let reminder = this.entityView.reminder;
    let prefilledNameConfirmed = false;
    if (this.entityView.newItem) {
      if (this.entityView.taskUuid || this.entityView.newEventFromSuggestion) {
        this.newFromTask = true;
        assignedToEmployees = [];
      }
      if (this.entityView.startTaskUuid) {
        date = LocalDate.now().toString();
      }
      // Prefill from a "Planlegg gjennomgang" creation (carried via URL params).
      if (this.prefillNameConfirmed) {
        prefilledNameConfirmed = true;
      }
      if (this.prefillTime !== undefined) {
        time = this.prefillTime;
      }
      if (this.prefillReminder !== undefined) {
        reminder = this.prefillReminder;
      }
    }
    let nameConfirmed = false;
    if (!this.entityView.newItem || this.newFromTask || prefilledNameConfirmed) {
      nameConfirmed = true;
    }
    return {
      alert: false,
      includeDetailsInAlert: false,
      message: '',
      saveType: 'single',
      type: 'standard',
      name: this.entityView.name,
      date,
      time,
      durationMinutes: this.entityView.durationMinutes,
      assignedToEmployees,
      assignedToFunction: this.entityView.assignedToFunction,
      assignedToContacts: this.entityView.assignedToContacts,
      procedures: this.entityView.procedures,
      notes: this.entityView.notes,
      taskUuid: this.entityView.taskUuid,
      assets: this.entityView.relatedAssets.map((a) => a.uuid),
      persistent: this.entityView.persistent,
      reminder,
      recurrence: '',
      existingRecurrences: 'KEEP',
      nameConfirmed,
    };
  }

  renderEditItem(item: EventOccurrenceViewEditItem): TemplateResult<1> {
    return html`
      <d-section topless>
        <d-edit-entity-name
          .autofocus=${!item.nameConfirmed && !this.newFromTask}
          .newItem=${this.entityView.newItem}
          .assets=${item.assets}
          .placeholder=${t('events.giveTaskName')}
          .parentHref=${this.parentHref}
          .name=${item.name}
          .originalName=${this.entityView.name}
          .currentEntityUuid=${this.entityView.uuid}
          .masterEntityUuid=${item.taskUuid}
          .models=${this.entityView.modelEvents.map((m): ModelForEntityName => {
            return {
              name: m.name,
              type: !m.taskUuid ? 'events' : 'tasks',
              masterEntityUuid: m.taskUuid,
              futureOccurrences: m.futureNotDoneOccurrences,
            };
          })}
          .taskOptions=${this.entityView.taskOptions}
          .disableTaskSelection=${this.entityView.startTaskUuid !== undefined ||
          this.prefillNameConfirmed ||
          this.entityView.newEventFromSuggestion}
          .bypassConfirmations=${item.nameConfirmed}
          @name-focused=${() => this.onNameFocused(item)}
          @cancel=${(e) => this.onNameCancel(e, item)}
          @done=${(e) => this.onNameDone(e, item)}
        ></d-edit-entity-name>
      </d-section>
      <d-smooth-resize>
        ${item.nameConfirmed
          ? html`
              <d-section>
                <d-edit-event-timing
                  .disableTodo=${this.entityView.startTaskUuid !== undefined}
                  .time=${item.time || 'NONE'}
                  .date=${item.date}
                  .durationMinutes=${item.durationMinutes}
                  .persistent=${item.persistent}
                  @value-changed=${(e: CustomEvent<EventTiming>) => {
                    e.stopPropagation();
                    if (e.detail.eventType === 'TODO') {
                      this.editItem = {
                        ...item,
                        date: '',
                        time: 'NONE',
                        durationMinutes: 0,
                        persistent: false,
                      };
                    } else {
                      this.editItem = {
                        ...item,
                        date: e.detail.date,
                        time: e.detail.time,
                        durationMinutes: e.detail.durationMinutes,
                        persistent: e.detail.persistent,
                      };
                    }
                  }}
                >
                </d-edit-event-timing>
              </d-section>
              ${this.singleUserVersion
                ? nothing
                : html`<d-section>
                    <d-edit-assignees
                      .assignedToFunction=${item.assignedToFunction}
                      .assignedToEmployees=${item.assignedToEmployees}
                      .assignedToContacts=${item.assignedToContacts}
                      .functions=${this.entityView.availableFunctions}
                      .employees=${this.entityView.availableEmployees}
                      .contacts=${this.entityView.availableContacts}
                      @assignment-changed=${(
                        e: CustomEvent<{
                          assignedToEmployees: string[];
                          assignedToFunction: string;
                          assignedToContacts: string[];
                        }>,
                      ) => {
                        e.stopPropagation();
                        this.editItem = {
                          ...item,
                          assignedToEmployees: e.detail.assignedToEmployees,
                          assignedToFunction: e.detail.assignedToFunction,
                          assignedToContacts: e.detail.assignedToContacts,
                        };
                      }}
                    >
                    </d-edit-assignees>
                  </d-section>`}
              <d-section>
                <d-edit-reminder
                  ?disabled=${item.time === 'NONE'}
                  .value=${item.reminder}
                  @value-changed=${(e: CustomEvent<{ value: ReminderType }>) => {
                    e.stopPropagation();
                    this.editItem = { ...item, reminder: e.detail.value };
                  }}
                >
                </d-edit-reminder>
              </d-section>
              <d-edit-html
                label-action-plain
                .contentStickyTop=${this.contentStickyTop}
                .showChecklist=${true}
                label="${t('events.procedure')}"
                .docsForLinking=${this.entityView.docsForLinking}
                sublabel="${this.procedureSubLabel(item)}"
                .value=${item.procedures}
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  this.editItem = { ...item, procedures: e.detail.value };
                }}
                .labelAction=${this.procedureAction(item)}
                @label-action=${() => {
                  this.editItem = { ...item, procedures: this.relatedTaskProcedure(item.taskUuid) };
                }}
              ></d-edit-html>
              <d-section topless>
                <d-edit-textarea
                  field="events_notes"
                  .value=${item.notes}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => {
                    e.stopPropagation();
                    this.editItem = { ...item, notes: e.detail.value };
                  }}
                ></d-edit-textarea>
              </d-section>
              ${this.entityView.assetOptions.length > 0
                ? html`
                    <d-section topless>
                      <d-select-checkbox
                        checklist
                        label-action-plain
                        label="${t('events.relatedAssets')}"
                        .opened=${this.showAssets(item)}
                        .labelAction=${this.assetsLabelAction(item)}
                        .options=${this.entityView.assetOptions.map((o) => {
                          return {
                            value: o.value,
                            text: o.text,
                            disabled: o.disabled,
                          };
                        })}
                        .value=${item.assets}
                        @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
                          e.stopPropagation();
                          this.editItem = { ...item, assets: e.detail.value };
                        }}
                        @label-action=${() => this.handleAssetLabelAction(item)}
                      ></d-select-checkbox>
                    </d-section>
                  `
                : nothing}
            `
          : nothing}
      </d-smooth-resize>
    `;
  }

  handleAssetLabelAction(item: EventOccurrenceViewEditItem) {
    if (!this.assetListOpened) {
      this.assetListOpened = true;
    } else if (this.deselectableDiscardedAssets(item).length > 0) {
      const assets = item.assets.filter((a) => {
        return !this.deselectableDiscardedAssets(item).includes(a);
      });
      this.editItem = { ...item, assets: assets };
    }
  }

  doneDisabled(): boolean {
    return this.editItem?.nameConfirmed === false || (this.editItem?.name ?? '') === '';
  }

  async openRecurrenceDialog() {
    const eventName = this.entityView.name;
    const parentUuid = '';
    let currentOccurrenceDateTime = this.entityView.date;
    if (this.entityView.time) {
      currentOccurrenceDateTime += ' ' + this.entityView.time;
    }
    const result: OccurrencesDialogResult = await DEditOccurrencesDialog.open({
      parentUuid,
      modelOccurrence: this.entityView.occurrences.find((o) => {
        return o.dateTime === currentOccurrenceDateTime;
      }) || { dateTime: '', classified: false, restricted: false },
      occurrences: this.entityView.occurrences,
      eventType: 'standard',
      eventName,
      collectionNames: this.entityView.relatedCollections.map((c) => c.name),
    });
    if (result.action === 'done') {
      this.fireRepeatEventOccurrenceEvent(result.occurrences, result.addToCollections);
    }
  }

  protected viewFields(): SectionField[] {
    return [
      /**
       * Skal bare vises i pdf
       */
      {
        field: 'timeDescription',
        fieldForLabel: 'time',
        type: 'view-text',
        class: 'fullWidth',
        condition: false,
      },
      {
        field: 'assigneeDisplayText',
        fieldForLabel: 'assignee',
        type: 'view-text',
        class: 'fullWidth',
        condition: !this.singleUserVersion,
      },
      {
        field: 'reminderText',
        fieldForLabel: 'reminder',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.reminderText !== '',
      },
      {
        type: 'custom',
        render: () => {
          return this.renderEditDone();
        },
      },
      {
        field: 'procedures',
        type: 'view-html',
        class: 'fullWidth',
        condition: !isEmptyHtmlContent(this.entityView.procedures),
      },
      {
        field: 'notes',
        type: 'view-text',
        class: 'fullWidth',
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'tasks_assets',
        sublabel: t('events.tapAssetToSetStatus'),
        icon: 'assets',
        class: 'related-assets',
        items: 'relatedAssetsAsListItems',
        condition: this.entityView.relatedAssets.length > 0,
      },
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('entityView')) {
      const oldView = _changedProperties.get('entityView');
      if (!oldView || oldView.uuid !== this.entityView.uuid) {
        this.setEditDone();
      }
    }
  }

  protected async checkForEditMode() {
    const params = new URLSearchParams(location.search.slice(1));
    // Capture prefill params (from e.g. the «Planlegg gjennomgang» action) before
    // the query is stripped below, so calculateEditItem can build the edit item
    // correctly from the start. nameConfirmed=true skips the routine/name step;
    // time + reminder make the review task ready to notify once saved.
    if (params.get('nameConfirmed') === 'true') {
      this.prefillNameConfirmed = true;
    }
    const prefillTime = params.get('time');
    if (prefillTime !== null) {
      this.prefillTime = prefillTime;
    }
    const prefillReminder = params.get('reminder');
    if (prefillReminder !== null) {
      this.prefillReminder = prefillReminder as ReminderType;
    }

    if (params.has('name')) {
      const name = params.get('name');
      if (params.has('edit') && this.entityView.href === location.pathname) {
        history.replaceState({}, '', location.protocol + '//' + location.host + location.pathname);
        await this.onEditModeOn();
        if (this.editItem) {
          this.editItem = { ...this.editItem, name: decodeURI(name || '') };
          setTimeout(() => {
            this.nameEditor.setNameSelected();
          }, 0);
        }
      }
    } else {
      await super.checkForEditMode();
    }
  }

  protected async saveEditItem(item: EventOccurrenceViewEditItem): Promise<EventOccurrenceViewEditItem | undefined> {
    let saveType: SaveType = 'single';
    if (this.entityView.canUpdateFutureOccurrences) {
      const result: RecurrenceOptionsResult = await RecurrenceOptionsDialog.open({
        isMeeting: false,
        deleting: false,
      });
      if (result.action === 'cancel') {
        return;
      } else if (result.action === 'following') {
        saveType = 'followingRepeated';
      } else {
        saveType = 'singleRepeated';
      }
    }
    let alert = false;
    let includeDetailsInAlert = false;
    let message = '';
    const originalEditItem = this.calculateEditItem();
    if (this.qualifiesForAlerts(originalEditItem)) {
      const r = this.alertOptionsAccessSettings(originalEditItem);
      const result: AlertsOnChangeResult = await AlertsOnChangeDialog.open({
        noAccessMessage: r.noAccessMessage,
        alertsDisabled: r.alertsDisabled,
        noDetails: !this.hasDetails(item),
        isDelete: false,
        newItem: this.entityView.newItem,
      });
      if (result.action === 'save-and-alert') {
        alert = true;
        includeDetailsInAlert = result.includeDetails;
        message = result.message;
      }
    }

    return {
      ...item,
      alert: alert,
      includeDetailsInAlert: includeDetailsInAlert,
      message: message,
      saveType: saveType,
    };
  }

  protected getIcon(): string {
    return 'events';
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  private renderEditDone() {
    const actionLabel = this.entityView.doneDate ? t('events.notDone') : t('events.markAsDone');
    return html` <d-wrap baseline style="margin: 0">
      <d-view-text inline-label .label=${this.entityView.doneLabel} .value=${this.entityView.doneText}></d-view-text>
      <d-action style="margin: -6px -4px" plain @click=${() => this.handleToggleDone()}>${actionLabel}</d-action>
      ${this.entityView.doneDate
        ? nothing
        : html` <d-wrap baseline tight style="flex-grow: 1">
            <d-wrap baseline tight style="flex-grow: 1">
              <d-select-date
                style="margin: 0"
                controller
                .value=${this.editDoneDate}
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  this.editDoneDate = e.detail.value;
                }}
              ></d-select-date>
              <d-select-dropdown
                style="flex: 1; max-width: 370px"
                controller
                inline-label
                light-label
                label="${t('events.by')}"
                .options=${this.entityView.doneByOptions}
                .value=${this.editDoneBy}
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  this.editDoneBy = e.detail.value;
                }}
              ></d-select-dropdown>
            </d-wrap>
          </d-wrap>`}
    </d-wrap>`;
  }

  private setEditDone() {
    this.editDoneBy = this.entityView.doneBy ?? this.entityView.currentUserUuid;
    let editDoneDate = this.entityView.today.toString();
    let editDoneBy = this.entityView.currentUserUuid;
    if (this.entityView.doneDate) {
      editDoneDate = this.entityView.doneDate;
      editDoneBy = this.entityView.doneBy;
    }
    this.editDoneDate = editDoneDate;
    this.editDoneBy = editDoneBy;
  }

  private renderMetaSection() {
    if (this.entityView.isConfirmedEntity) {
      const e: EventOccurrenceView = this.entityView;
      return html`
        <div class="meta-section">
          ${e.taskHref
            ? html`
                <div class="related-task">
                  <d-label semibold inline-label label="${t('events.relatedToTask')}" .value=${e.taskName}></d-label>
                  <d-action plain theme-page href="${shortenPath(e.taskHref) ?? ''}">${e.taskName}</d-action>
                </div>
              `
            : nothing}
          <d-view-occurrences
            .writeAccess=${this.entityView.currentUserHasWriteAccess}
            .occurrences=${e.occurrences}
            .eventType=${'standard'}
            .parentHref=${e.parentHref}
            .currentUuid=${e.uuid}
            style="margin-bottom: -12px"
            @edit-occurrences=${() => this.openRecurrenceDialog()}
          ></d-view-occurrences>
        </div>
      `;
    }
    return nothing;
  }

  private deselectableDiscardedAssets(item: EventOccurrenceViewEditItem): string[] {
    return this.entityView.assetOptions
      .filter((o) => {
        return !o.disabled && o.discarded && item.assets.includes(o.value);
      })
      .map((o) => {
        return o.value;
      });
  }

  private assetsLabelAction(item: EventOccurrenceViewEditItem) {
    if (!this.showAssets(item)) {
      return t('events.addAsset');
    }
    if (this.deselectableDiscardedAssets(item).length > 0) {
      return t('events.removeDiscardedAssets');
    }
    return '';
  }

  private onNameFocused(item) {
    this.editItem = { ...item, nameConfirmed: false };
  }

  private onNameCancel(e, item) {
    e.stopPropagation();
    if (this.entityView.newItem) {
      this.onDelete();
    } else {
      this.editItem = { ...item, name: this.entityView.name, nameConfirmed: true };
    }
  }

  private onNameDone(e, item) {
    e.stopPropagation();
    const name = e.detail.name;
    const taskUuid = e.detail.masterEntityUuid;
    const editItem = {
      ...item,
      name,
      taskUuid,
      nameConfirmed: true,
    };
    if (name !== this.entityView.name || taskUuid !== this.entityView.taskUuid) {
      let modelEvent: ModelEvent | undefined = undefined;
      if (name !== this.entityView.name) {
        modelEvent = this.entityView.modelEvents.find((m) => {
          return m.name === e.detail.name;
        });
      }
      if (modelEvent) {
        editItem.taskUuid = modelEvent.taskUuid;
        editItem.procedures = modelEvent.procedures;
        editItem.notes = modelEvent.notes;
        editItem.time = modelEvent.time;
        editItem.durationMinutes = modelEvent.durationMinutes;
        if (editItem.time !== 'NONE') {
          editItem.date = LocalDate.now().plusDays(1).toString();
        }
        if (modelEvent.assignedToFunction) {
          editItem.assignedToFunction = modelEvent.assignedToFunction;
          editItem.assignedToEmployees = [];
          editItem.assignedToContacts = [];
        }
        if (modelEvent.assignedToEmployees.length) {
          editItem.assignedToFunction = '';
          editItem.assignedToEmployees = modelEvent.assignedToEmployees;
          editItem.assignedToContacts = [];
        }
        if (modelEvent.assignedToContacts.length) {
          editItem.assignedToFunction = '';
          editItem.assignedToEmployees = [];
          editItem.assignedToContacts = modelEvent.assignedToContacts;
        }
      }
    }
    this.editItem = editItem;
  }

  private fireRepeatEventOccurrenceEvent(occurrences: Occurrence[], applyToCollections: boolean) {
    console.log('applyToCollections', applyToCollections);
    const existingDateTimes = this.entityView.occurrences.map((e) => toDateTimeDuration(e.dateTime, e.durationMinutes));
    const existingUuids = this.entityView.occurrences.map((e) => e.occurrenceUuid);
    const updatedOccurrenceUuids = occurrences.map((e) => e.occurrenceUuid);
    this.dispatchEvent(
      new CustomEvent<{
        uuid: string;
        dateTimesToAdd: string[];
        occurrencesToRemove: string[];
        applyToCollections: boolean;
      }>('repeat-event-occurrence', {
        bubbles: true,
        composed: true,
        detail: {
          uuid: this.entityView.uuid,
          dateTimesToAdd: occurrences
            .map((e) => toDateTimeDuration(e.dateTime, e.durationMinutes))
            .filter((e) => !existingDateTimes.includes(e)),
          occurrencesToRemove: existingUuids.filter((e) => !updatedOccurrenceUuids.includes(e)),
          applyToCollections: applyToCollections,
        },
      }),
    );
  }

  private handleToggleDone() {
    let done = true;
    if (this.entityView.doneDate) {
      done = false;
    }
    this.dispatchEvent(
      new CustomEvent<EventDoneDetail>('toggle-event-done', {
        bubbles: true,
        composed: true,
        detail: {
          done,
          uuid: this.entityView.uuid,
          eventUuid: this.entityView.eventUuid,
          eventInstance: this.entityView.eventInstance,
          doneBy: this.editDoneBy,
          doneDate: this.editDoneDate,
        },
      }),
    );
    if (this.entityView.startTaskUuid !== undefined && done) {
      this.dispatchEvent(
        new CustomEvent<{ taskUuid: string }>('task-done', {
          bubbles: true,
          composed: true,
          detail: {
            taskUuid: this.entityView.startTaskUuid,
          },
        }),
      );
    }
    if (!done) {
      this.editDoneDate = LocalDate.now().toString();
      this.editDoneBy = this.entityView.currentUserUuid;
    }
  }

  private onlyAssignedToCurrentUser(originalEditItem: EventOccurrenceViewEditItem) {
    return (
      this.currentAndPreviousAssignees(originalEditItem).length === 1 &&
      this.currentAndPreviousAssignees(originalEditItem)[0] === this.entityView.currentUserUuid
    );
  }

  private currentAndPreviousAssignees(originalEditItem: EventOccurrenceViewEditItem) {
    const allAssignees = [...this.assignees(originalEditItem), ...this.assignees(this.editItem)];
    return uniq(allAssignees);
  }

  private editsShouldAlert(item: EventOccurrenceViewEditItem) {
    const originalEditItem = item;
    return (
      originalEditItem !== undefined &&
      this.editItem !== undefined &&
      (originalEditItem.name !== this.editItem.name ||
        originalEditItem.date !== this.editItem.date ||
        originalEditItem.time !== this.editItem.time ||
        originalEditItem.durationMinutes !== this.editItem.durationMinutes ||
        originalEditItem.notes !== this.editItem.notes ||
        originalEditItem.procedures !== this.editItem.procedures)
    );
  }

  private procedureSubLabel(item: EventOccurrenceViewEditItem) {
    if (
      item.taskUuid &&
      !isEmptyHtmlContent(this.relatedTaskProcedure(item.taskUuid)) &&
      isSameHtmlContent(item.procedures, this.relatedTaskProcedure(item.taskUuid))
    ) {
      return t('events.fromRelatedTask');
    }
    return '';
  }

  private procedureAction(item: EventOccurrenceViewEditItem) {
    if (
      item.taskUuid &&
      !isEmptyHtmlContent(this.relatedTaskProcedure(item.taskUuid)) &&
      !isSameHtmlContent(item.procedures, this.relatedTaskProcedure(item.taskUuid))
    ) {
      return t('events.getFromRelatedTask');
    }
    return '';
  }

  private showAssets(item: EventOccurrenceViewEditItem): boolean {
    return this.assetListOpened || item.assets.length > 0;
  }

  private assignees(item?: EventOccurrenceViewEditItem) {
    if (item === undefined) return [];
    let result: string[] = [];
    if (item.assignedToEmployees.length > 0) {
      result = [...item.assignedToEmployees];
    }
    if (item.assignedToFunction !== '') {
      if (item.assignedToFunction === this.entityView.commonFunctionUuid) {
        this.entityView.availableEmployees.forEach((e) => {
          result.push(e.value);
        });
      } else {
        this.entityView.availableFunctions
          .filter((x) => x.value === item.assignedToFunction)
          .flatMap((x) => x.employees)
          .forEach((x) => {
            result.push(x);
          });
      }
    }
    if (item.assignedToContacts.length > 0) {
      item.assignedToContacts.forEach((c) => {
        result.push(c);
      });
    }
    if (result.length === 0) {
      console.error('Unassigned event occurrence');
    }
    return uniq(result);
  }

  private qualifiesForAlerts(originalEditItem: EventOccurrenceViewEditItem): boolean {
    assertIsDefined(this.editItem);
    if (
      (this.entityView.newItem || this.editsShouldAlert(originalEditItem)) &&
      !this.onlyAssignedToCurrentUser(originalEditItem)
    ) {
      return true;
    }
    return this.otherAssigneesChanged(originalEditItem);
  }

  private otherAssigneesChanged(originalEditItem: EventOccurrenceViewEditItem) {
    assertIsDefined(this.editItem);
    const prevExCurrentUser = this.assignees(originalEditItem).filter(
      (item) => item !== this.entityView.currentUserUuid,
    );
    const newExCurrentUser = this.assignees(this.editItem).filter((item) => item !== this.entityView.currentUserUuid);
    return xor(prevExCurrentUser, newExCurrentUser).length !== 0;
  }

  private relatedTaskProcedure(uuid: string) {
    if (uuid) {
      const task = this.entityView.modelEvents.find((t) => {
        return t.taskUuid === uuid;
      });
      if (task) {
        return task.procedures;
      }
    }
    return '';
  }

  private fireDeleteEvent(saveType: SaveType, alert: boolean) {
    this.dispatchEvent(
      new CustomEvent<{ saveType: SaveType; alert: boolean }>('delete-event', {
        bubbles: true,
        composed: true,
        detail: { saveType: saveType, alert: alert },
      }),
    );
    this.dispatchEvent(new CustomEvent('edit-off', { bubbles: true, composed: true }));
  }

  private qualifiesForDeleteAlerts(originalEditItem: EventOccurrenceViewEditItem): boolean {
    return !this.onlyAssignedToCurrentUser(originalEditItem) && !this.entityView.newItem;
  }

  private alertOptionsAccessSettings(originalEditItem: EventOccurrenceViewEditItem): {
    noAccessMessage: string;
    alertsDisabled: boolean;
  } {
    let message = '';
    const allAssigned = this.currentAndPreviousAssignees(originalEditItem);
    let alertsDisabled = false;
    const accessRecipients = allAssigned
      .filter((id) => id !== this.entityView.currentUserUuid)
      .filter((id) => {
        const p = this.entityView.messageRecipients.find((x) => x.uuid === id);
        assertIsDefined(p);
        return p.accessLevel !== 'NONE';
      });
    const noAccessRecipients = allAssigned
      .filter((id) => id !== this.entityView.currentUserUuid)
      .filter((id) => {
        const p = this.entityView.messageRecipients.find((x) => x.uuid === id);
        assertIsDefined(p);
        return p.accessLevel === 'NONE';
      });

    if (noAccessRecipients.length) {
      const list = this.entityView.messageRecipients
        .filter((p) => noAccessRecipients.includes(p.uuid))
        .map((p) => p.name);
      message = t('events.recipientsMissingAccess', { names: joinWithAnd(list) });
    }
    if (!accessRecipients.length) {
      message += t('events.noOneCanBeNotified');
      alertsDisabled = true;
    }
    return {
      alertsDisabled,
      noAccessMessage: message,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-event-occurrence-view': DEventOccurrenceView;
  }
}
